import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import { PushNotificationsDialogs, usePushNotifications } from '@pegasus/push-messages';
import { useTranslationContext } from '@pegasus/translation';
import { useUpcomingPushMessages } from '@pegasus/upcoming-section';
import { useConfig } from '@browsing/config';
import { useQuery } from '@browsing/hooks';

import {
    EarlyAccessPhase,
    PlusEducationalDialog,
    PlusMembershipStatus,
    useEarlyAccessPhase,
} from '@browsing/plus';
import { getCookie } from '@browsing/utils';

import { track } from '@client/helpers/tracking';
import { getFirstName } from '@client/redux/modules/customer';
import {
    showHeaderErrorFlashMessage,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import {
    getShowstopperEarlyAccessCampaigns,
    hasPlusEarlyAccessTab,
    pickEarlyAccess,
} from '@client/redux/modules/mylounge';
import { getIsMobileScreen } from '@client/redux/modules/clientDevice/selectors';
import { useVariant } from '@client/lib/assignments';
import { PLUS_EARLY_ACCESS_TAB_ID } from '@client/config/constants';

import { getClientDeviceWidth } from '@client/redux/modules/clientDevice';

import { useZalandoPlus } from '../use-zalando-plus';
import { usePlusNewCampaignCard } from '../use-plus-new-campaing-card';

const RebrandedPlusShowstopper = loadable(
    () => import(/* webpackChunkName: "browsing-plus-showstopper" */ '@browsing/plus'),
    {
        resolveComponent: components => components.PlusRebrandedShowstopper,
    },
);

const CampaignCardV2 = loadable(
    () => import(/* webpackChunkName: "browsing-plus-campaign-card-v2" */ '@browsing/plus'),
    {
        resolveComponent: components => components.CampaignCardV2,
    },
);

const handleOnTrack = (trackEvent: string, data: string) =>
    track(trackEvent, { productCampaign: data });

export const EarlyAccessSection = memo(
    ({
        marginLess,
        renderWhenPhases = [],
    }: {
        marginLess?: boolean;
        renderWhenPhases: EarlyAccessPhase[];
    }) => {
        const dispatch = useDispatch();
        const config = useConfig();
        const query = useQuery();
        const history = useHistory();
        const { translations } = useTranslationContext();
        const isMobileScreen = useSelector(getIsMobileScreen);
        const plusEarlyAccessTabShown = useSelector(hasPlusEarlyAccessTab);
        const { isSignupEnabled, status, type } = useZalandoPlus();
        const width = useSelector(getClientDeviceWidth);
        const normalHeight = width * 0.58;
        const firstName = useSelector(getFirstName);
        const { openCampaigns, upcomingCampaigns } = useSelector(
            getShowstopperEarlyAccessCampaigns,
        );
        const isPlusNewCampaignCardEnabled = usePlusNewCampaignCard();
        const isThbResizeWidthVariant = useVariant('thb_max_width', 'variant_thb_width');
        const signupFilteredUpcomingCampaigns = useMemo(() => {
            if (isSignupEnabled) {
                return openCampaigns.length !== 0 ? [] : upcomingCampaigns.filter(pickEarlyAccess);
            }

            return upcomingCampaigns;
        }, [isSignupEnabled, openCampaigns, upcomingCampaigns]);

        const isNotificationEnabled = useMemo(() => {
            const featureEnabled = config.isEnabled('pushNotification');

            return featureEnabled || getCookie('zl_web_push') === 'true';
        }, [config]);

        const handlePushAPIActivationFinished = useCallback(
            (isActivated: boolean, message: string) => {
                const showFlashMessageFn = isActivated
                    ? showHeaderSuccessFlashMessage
                    : showHeaderErrorFlashMessage;

                dispatch(showFlashMessageFn(message));
            },
            [dispatch],
        );

        const { isSupported, registerUser, checkAccess, dialogControls } = usePushNotifications();

        const isWebPushActive = isNotificationEnabled && isSupported;

        const { subscribeToReminder, subscribedCampaigns, registerToReminder, pendingCampaignId } =
            useUpcomingPushMessages(
                upcomingCampaigns,
                config.appdomain,
                config.notificationTagSuffix,
                handlePushAPIActivationFinished,
                checkAccess,
                registerUser,
                true,
            );

        const notificationDialog = isWebPushActive ? (
            <PushNotificationsDialogs<string>
                dialogControls={dialogControls}
                translations={translations}
                firstName={firstName}
                data={pendingCampaignId}
                onRegister={registerToReminder}
                onTrack={handleOnTrack}
            />
        ) : null;

        const earlyAccessPhase = useEarlyAccessPhase(openCampaigns, upcomingCampaigns);

        const activePlusEarlyAccessTab = () => {
            history.push(`/event/${PLUS_EARLY_ACCESS_TAB_ID}${window.location.search}`);
        };

        const handlePlusEducationalDialogError = (message: string) => {
            dispatch(showHeaderErrorFlashMessage(message));
        };

        const shouldRenderPlusNewCampaignCard = () =>
            isPlusNewCampaignCardEnabled && isMobileScreen && openCampaigns.length > 0;

        if (!status) {
            return null;
        }

        if (status === PlusMembershipStatus.NotEligible) {
            return null;
        }

        if (!renderWhenPhases.includes(earlyAccessPhase)) {
            return null;
        }

        if (shouldRenderPlusNewCampaignCard()) {
            const [fistOpenCampaign] = openCampaigns;

            return (
                <CampaignCardV2
                    // TODO: remove ts-ignore
                    // @ts-ignore
                    name={fistOpenCampaign.name}
                    image={fistOpenCampaign.images.mobile}
                    endsAt={fistOpenCampaign.plusEndsAt}
                    id={fistOpenCampaign.campaignId}
                    discount={fistOpenCampaign.discount}
                    imageHeight={normalHeight}
                    plusMembershipStatus={status}
                />
            );
        }

        return (
            <>
                {notificationDialog}
                <PlusEducationalDialog
                    context="LOUNGE_MYLOUNGE"
                    isMobileScreen={isMobileScreen}
                    onError={handlePlusEducationalDialogError}
                >
                    <RebrandedPlusShowstopper
                        earlyAccessPhase={earlyAccessPhase}
                        isWebPushActive={isWebPushActive}
                        marginLess={marginLess}
                        openCampaigns={openCampaigns}
                        plusMembershipStatus={status}
                        plusMembershipType={type}
                        subscribedCampaigns={subscribedCampaigns}
                        subscribeToReminder={subscribeToReminder}
                        upcomingCampaigns={signupFilteredUpcomingCampaigns}
                        queryParams={query}
                        isMobileScreen={isMobileScreen}
                        normalHeight={normalHeight}
                        isThbWidthResizeVariant={isThbResizeWidthVariant}
                        activePlusEarlyAccessTab={activePlusEarlyAccessTab}
                        plusEarlyAccessTabShown={plusEarlyAccessTabShown}
                    />
                </PlusEducationalDialog>
            </>
        );
    },
);
